import { DOCUMENT } from '@angular/common';
import { computed, Injectable, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { type Driver, type DriveStep, driver } from 'driver.js';
import { forkJoin, type Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import { AuthenticationService } from '@app/auth/services/authentication.service';
import {
    type GgxTourDefinition,
    type GgxTourStep,
    getTourDefinition,
    TOUR_REGISTRY,
} from '@app/modules/learning-hub/constants/tour-registry';
import {
    LearningHubService,
    type LearningTourAdapter,
} from '@app/modules/learning-hub/services/learning-hub.service';
import {
    featherOverlay,
    fitMediaTo,
    fitPopover,
    SectionProxy,
    showIntro,
    showOutro,
    stageShapeFor,
    styleStepCard,
    TargetMarker,
    TourRail,
} from '@app/modules/learning-hub/services/tour-popover-theme';
import { AppConfigService } from '@app/shared/services/app-config.service';
import { CommonAPIService } from '@app/shared/services/common-api.service';
import { RecentActivityService } from '@app/shared/services/recent-activity.service';
import { ToastNotificationService } from '@app/shared/services/toast-notification.service';
import type { GlobalSearchItem } from '@app/theme/layouts/header-nav/global-search/global-search.component';

/**
 * Feature flag for the code-owned Driver.js provider.
 *
 * Product Fruits stays installed and functional while the migration is piloted; flip
 * this to `false` to fall back to it entirely.
 */
const USE_DRIVER_TOURS = true;

const PROGRESS_KEY_PREFIX = 'ggx.tour.progress';

export interface TourLaunchOptions {
    required?: boolean;
    resumeFrom?: number;
    /** Set once the tour's project is selected, so the project switch happens only once. */
    projectReady?: boolean;
}

interface TourSession {
    definition: GgxTourDefinition;
    tour: LearningTourAdapter;
    required: boolean;
    screenOpen: boolean;
    removeScreen: (() => void) | null;
    driver: Driver | null;
    rail: TourRail | null;
    marker: TargetMarker | null;
    proxy: SectionProxy | null;
    activeIndex: number;
    anchorWatch: MutationObserver | null;
    paused: boolean;
    completing: boolean;
    finished: boolean;
    catalogue: LearningTourAdapter[];
    onKey: ((e: KeyboardEvent) => void) | null;
    onResize: (() => void) | null;
    startedAt: number;
    ready: Promise<void>;
}

/**
 * Provider-agnostic boundary for in-app product tours.
 *
 * The Learning Hub UI gates on this service rather than on any one vendor, so the
 * underlying tour provider can be swapped without touching the components. Product
 * Fruits remains the provider for tours without a code-owned definition.
 *
 * A code-owned tour runs as: intro screen → Driver steps under the pinned rail →
 * finish screen. Tracking keeps using the GGX `LearningTour.id`, so `tour_tracking`
 * history and Slack notifications continue unchanged.
 */
@Injectable({
    providedIn: 'root',
})
export class TourService {
    private readonly appConfigService = inject(AppConfigService);
    private readonly document = inject(DOCUMENT);
    private readonly router = inject(Router);
    private readonly auth = inject(AuthenticationService);
    private readonly learningHub = inject(LearningHubService);
    private readonly toast = inject(ToastNotificationService);
    private readonly commonApi = inject(CommonAPIService);
    private readonly recentActivity = inject(RecentActivityService);

    /** True once the active provider has finished initialising and can list tours. */
    readonly isReady = signal(false);

    /** Product Fruits project id, when that provider is configured. */
    readonly productFruitsId = computed(() => this.appConfigService.appConfig().productFruits);

    /**
     * Whether the Learning Hub entry points are shown.
     *
     * Deliberately not tied to Product Fruits: the catalogue, descriptions and groups
     * come from the GGX database, so the hub is useful even with no provider attached.
     */
    readonly isLearningHubEnabled = computed(
        () => this.appConfigService.appConfig().enableLearningHub ?? false,
    );

    /** Whether a provider can supply per-user completion state for the tour list. */
    readonly hasProviderState = computed(() => Boolean(this.productFruitsId()) && this.isReady());

    /** The code-owned provider, active only while the migration flag is on. */
    readonly isDriverEnabled = computed(() => USE_DRIVER_TOURS);

    readonly isActive = signal(false);

    private session: TourSession | null = null;
    /** Selectors with a re-anchor wait in flight, so waiters are not stacked. */
    private readonly reanchoring = new Set<string>();
    /** Destinations with an advance-watcher already armed. */
    private readonly armed = new Set<string>();
    /** Router subscriptions held so a finished tour cannot advance a later one. */
    private navigationSubs: Subscription[] = [];
    private firstRunChecked = false;
    private settingsClosing = false;
    private projectOpenListener: ((event: Event) => void) | null = null;

    /** Whether a code-owned definition exists for this tour key. */
    hasDefinition(key: string): boolean {
        return this.isDriverEnabled() && Boolean(key) && Boolean(getTourDefinition(key));
    }

    launchTour(tour: LearningTourAdapter, options: TourLaunchOptions = {}): boolean {
        const definition = getTourDefinition(tour.key);
        if (!this.isDriverEnabled() || !definition) return false;

        this.learningHub.closeDialog();

        // Tours built on a project select it first, so their steps find the objects they open.
        const project = definition.project;
        if (project && !options.projectReady && this.readSelectedProjectSlug() !== project.slug) {
            void this.switchProject(project, () =>
                this.launchTour(tour, { ...options, projectReady: true }),
            );
            return true;
        }

        const gate = definition.prerequisite;
        if (gate && (!this.resolveTarget(gate.target) || !this.onEntryRoute(definition.route))) {
            void this.openEntryPoint(definition, () => this.launchTour(tour, options));
            return true;
        }

        this.stop();

        const required = options.required ?? false;
        const resumeFrom = options.resumeFrom;
        this.session = {
            definition,
            tour,
            required,
            screenOpen: false,
            removeScreen: null,
            driver: null,
            rail: null,
            marker: null,
            proxy: null,
            activeIndex: 0,
            anchorWatch: null,
            paused: false,
            completing: false,
            finished: false,
            catalogue: [],
            onKey: null,
            onResize: null,
            startedAt: Date.now(),
            ready: Promise.resolve(),
        };
        this.isActive.set(true);
        const seeded = this.seedRecentActivity(definition);
        this.session.ready = Promise.race([
            seeded,
            new Promise<void>(resolve => globalThis.setTimeout(resolve, 4000)),
        ]);

        this.learningHub
            .getToursList()
            .pipe(take(1))
            .subscribe({
                next: ({ result }) => {
                    if (this.session?.tour.id === tour.id) this.session.catalogue = result;
                },
                error: () => undefined,
            });

        if (resumeFrom === undefined) {
            this.learningHub
                .trackTourStarted(tour.id, tour.title)
                .pipe(take(1))
                .subscribe({
                    error: error => console.error('Failed to record tour start', error),
                });
        }

        if (definition.intro && resumeFrom === undefined) {
            this.openIntro();
        } else {
            this.beginSteps(resumeFrom ?? 0);
        }
        return true;
    }

    startFirstRunIfNeeded(): void {
        if (!this.isDriverEnabled() || this.firstRunChecked || this.session) return;
        this.firstRunChecked = true;

        const requiredKeys = Object.values(TOUR_REGISTRY)
            .filter(definition => definition.required)
            .map(definition => definition.key);
        if (requiredKeys.length === 0) return;

        // FIXME: No way to unsubscribe this part ?
        forkJoin({
            tours: this.learningHub.getToursList().pipe(take(1)),
            completed: this.learningHub.getCompletedTours().pipe(take(1)),
        }).subscribe({
            next: ({ tours, completed }) => {
                if (this.session) return;
                const completedIds = new Set(completed.result.tourIds);
                const tour = tours.result.find(
                    t => requiredKeys.includes(t.key) && !completedIds.has(t.id),
                );
                if (!tour) return;
                const resumeFrom = this.readProgress(tour.key);
                this.launchTour(tour, { required: true, resumeFrom });
            },
            error: error => console.error('Could not check onboarding status', error),
        });
    }

    private async seedRecentActivity(definition: GgxTourDefinition): Promise<void> {
        const names = new Set<string>();
        for (const step of definition.steps) {
            if (step.target?.startsWith('[test-id^="recent-activity-item-"]') && step.targetText) {
                names.add(step.targetText);
            }
        }
        if (names.size === 0) return;
        const present = new Set(
            this.recentActivity.getRecentActivities().map(entry => entry.details.name),
        );
        for (const name of names) {
            if (present.has(name)) continue;
            try {
                const { result } = await new Promise<{ result: GlobalSearchItem[] }>(
                    (resolve, reject) =>
                        this.commonApi
                            .getGlobalSearch<GlobalSearchItem>({ keyword: name, take: 10 }, false)
                            .pipe(take(1))
                            .subscribe({ next: resolve, error: reject }),
                );
                const match =
                    result.find(item => item.name === name) ??
                    result.find(item => item.name.includes(name));
                if (!match) continue;
                this.recentActivity.addActivity({
                    objectType: match.objectType,
                    timestamp: new Date().toISOString(),
                    details: match,
                });
            } catch {
                // Recent activity seeding is best-effort; a search failure should not block the tour.
            }
        }
    }

    private openIntro(): void {
        const session = this.session;
        if (!session) return;
        this.closeScreen();
        this.lockScroll(true);
        session.screenOpen = true;
        session.removeScreen = showIntro(this.document, {
            definition: session.definition,
            eyebrow: this.trackName(session.tour),
            userName: this.auth.currentUser()?.name ?? 'there',
            totalSteps: this.totalSteps(session.definition),
            onBegin: () => {
                void session.ready.then(() => {
                    if (this.session !== session || !session.screenOpen) return;
                    this.closeScreen();
                    this.beginSteps(0);
                });
            },
            onSkip: session.required
                ? undefined
                : () => {
                      this.closeScreen();
                      this.pause();
                  },
        });
    }

    private openOutro(): void {
        const session = this.session;
        if (!session) return;
        this.closeScreen();
        this.lockScroll(true);
        session.screenOpen = true;

        const next = this.nextTour(session);
        const nextDefinition = next ? getTourDefinition(next.key) : undefined;
        const openHub = () => {
            this.finish();
            this.learningHub.openDialog();
        };

        session.removeScreen = showOutro(this.document, {
            definition: session.definition,
            totalSteps: this.totalSteps(session.definition),
            elapsedMs: Date.now() - session.startedAt,
            next: next
                ? {
                      title: this.shortTitle(next.title),
                      minutes: nextDefinition?.estimatedMinutes,
                      steps: nextDefinition ? this.totalSteps(nextDefinition) : undefined,
                  }
                : undefined,
            primaryLabel: nextDefinition
                ? 'Start next tour'
                : next
                  ? 'Open the Learning Hub'
                  : 'Back to the Learning Hub',
            onPrimary: () => {
                if (next && nextDefinition) {
                    this.finish();
                    this.launchTour(next);
                } else {
                    openHub();
                }
            },
            secondaryLabel: nextDefinition ? 'Back to Learning Hub' : 'Explore on my own',
            onSecondary: () => {
                if (nextDefinition) {
                    openHub();
                } else {
                    this.finish();
                }
            },
        });
    }

    private closeScreen(): void {
        const session = this.session;
        if (!session) return;
        session.removeScreen?.();
        session.removeScreen = null;
        session.screenOpen = false;
        this.lockScroll(false);
    }

    private lockScroll(locked: boolean): void {
        this.document.body.style.overflow = locked ? 'hidden' : '';
    }

    private totalSteps(definition: GgxTourDefinition): number {
        return definition.steps.length + (definition.intro ? 1 : 0);
    }

    private stepNumber(definition: GgxTourDefinition, index: number): number {
        return index + (definition.intro ? 2 : 1);
    }

    private beginSteps(startIndex: number): void {
        const session = this.session;
        if (!session) return;
        const { definition } = session;
        const doc = this.document;

        session.marker = new TargetMarker(
            doc,
            () => this.placeCard(),
            () => {
                const d = session.driver;
                if (d?.isActive()) d.refresh();
            },
            () => this.cardRect(),
        );
        session.proxy = new SectionProxy(doc, () => {
            const d = session.driver;
            if (d?.isActive()) {
                d.refresh();
                this.placeCard();
            }
        });
        if ((definition.chapters?.length ?? 0) > 0 || definition.intro) {
            session.rail = new TourRail(doc, {
                title: this.shortTitle(session.tour.title),
                chapters: definition.chapters ?? [],
                totalSteps: this.totalSteps(definition),
                resumable: session.required,
                onPause: () => this.pause(),
            });
        }

        const instance = driver({
            showProgress: false,
            animate: false,
            allowClose: false,
            allowKeyboardControl: false,
            disableActiveInteraction: false,
            overlayClickBehavior: (_element, _step, { driver: d }) => {
                if (!definition.steps[d.getActiveIndex() ?? 0]?.advanceWhenAbsent) return;
                this.document.body.dispatchEvent(new MouseEvent('click', { bubbles: true }));
            },
            overlayColor: '#0b1220',
            overlayOpacity: 0.72,
            stagePadding: 10,
            stageRadius: 16,
            popoverOffset: 22,
            smoothScroll: false,
            nextBtnText: 'Continue',
            prevBtnText: 'Back',
            doneBtnText: 'Continue',
            steps: definition.steps.map((step, i) => this.toDriveStep(step, i, definition.steps)),
            onPopoverRender: (dom, { state }) => {
                const index = state.activeIndex ?? 0;
                const step = definition.steps[index];
                if (!step) return;
                session.activeIndex = index;
                this.setPopoverHidden(false);
                const number = this.stepNumber(definition, index);
                const chapterIndex =
                    definition.chapters?.findIndex(c => c.steps.includes(number)) ?? -1;
                const isAction = Boolean(step.advanceOnTargetClick);
                styleStepCard(dom, {
                    doc,
                    step,
                    chapter: chapterIndex >= 0 ? definition.chapters?.[chapterIndex] : undefined,
                    chapterIndex: chapterIndex >= 0 ? chapterIndex : undefined,
                    isAction,
                    stepNumber: number,
                    totalSteps: this.totalSteps(definition),
                    onRelayout: () => {
                        if (session.driver?.isActive()) session.driver.refresh();
                    },
                });
                session.rail?.update(number);
                if (session.required) this.saveProgress(definition.key, index);
            },
            onHighlightStarted: (element, _step, { state, driver: d }) => {
                featherOverlay(state.__overlaySvg);
                if (element) {
                    const { padding, radius } = stageShapeFor(element);
                    d.setConfig({ ...d.getConfig(), stagePadding: padding, stageRadius: radius });
                }
            },
            onHighlighted: (_element, _driveStep, { state, driver: d }) => {
                featherOverlay(state.__overlaySvg);
                const resync = () => {
                    if (session.driver === d && d.isActive()) {
                        d.refresh();
                        this.placeCard();
                        requestAnimationFrame(() => this.placeCard());
                    }
                };
                requestAnimationFrame(resync);
                globalThis.setTimeout(resync, 120);
                globalThis.setTimeout(resync, 320);
                let frames = 60;
                const settle = () => {
                    if (session.driver !== d || !d.isActive()) return;
                    this.placeCard();
                    if (frames-- > 0) requestAnimationFrame(settle);
                };
                requestAnimationFrame(settle);
                globalThis.setTimeout(resync, 1400);
                this.applyActiveStep();
            },
            onDeselected: () => session.marker?.clear(),
            onPrevClick: (_el, _step, { state, driver: d }) => {
                if ((state.activeIndex ?? 0) === 0 && definition.intro) {
                    this.backToIntro();
                } else {
                    d.movePrevious();
                }
            },
            onDoneClick: () => this.completeSteps(),
            onDestroyed: () => {
                session.marker?.clear();
                if (session.paused || session.completing) return;
                this.finish();
            },
        });

        session.driver = instance;
        session.onKey = (e: KeyboardEvent) => {
            if (session.screenOpen || !session.driver?.isActive()) return;
            const target = e.target as HTMLElement | null;
            if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
            const index = session.driver.getActiveIndex() ?? 0;
            const step = definition.steps[index];
            if (e.key === 'ArrowRight' || e.key === 'Enter') {
                if (step?.advanceOnTargetClick) return;
                e.preventDefault();
                if (session.driver.isLastStep()) this.completeSteps();
                else session.driver.moveNext();
            } else if (e.key === 'ArrowLeft') {
                e.preventDefault();
                if (index === 0 && definition.intro) this.backToIntro();
                else session.driver.movePrevious();
            }
        };
        doc.addEventListener('keydown', session.onKey);
        session.onResize = () => globalThis.requestAnimationFrame(() => this.placeCard());
        window.addEventListener('resize', session.onResize);
        doc.addEventListener('scroll', session.onResize, true);

        session.rail?.show();
        instance.drive(Math.min(startIndex, definition.steps.length - 1));
    }

    private backToIntro(): void {
        const session = this.session;
        if (!session) return;
        session.completing = true; // suppress the destroy hook's finish
        this.teardownSteps();
        session.completing = false;
        this.clearProgress(session.definition.key);
        this.openIntro();
    }

    private completeSteps(): void {
        const session = this.session;
        if (!session) return;
        session.completing = true;
        this.teardownSteps();
        if (session.definition.outro) {
            this.openOutro();
        } else {
            this.finish();
        }
    }

    private pause(): void {
        const session = this.session;
        if (!session) return;
        session.paused = true;
        if (session.required) {
            this.saveProgress(session.definition.key, session.driver?.getActiveIndex() ?? 0);
        } else {
            this.clearProgress(session.definition.key);
        }
        this.teardownSteps();
        this.isActive.set(false);
        this.session = null;
        this.toast.showInfo(
            session.required
                ? 'Tour paused. It picks up from this step next time you sign in.'
                : 'Tour closed. You can restart it any time from the Learning Hub.',
        );
    }

    private finish(): void {
        const session = this.session;
        if (!session) return;
        if (!session.finished) {
            session.finished = true;
            this.learningHub
                .trackTourFinished(session.tour.id, session.tour.title)
                .pipe(take(1))
                .subscribe({
                    error: error => console.error('Failed to record tour completion', error),
                });
        }
        this.clearProgress(session.definition.key);
        this.closeScreen();
        this.teardownSteps();
        this.isActive.set(false);
        this.session = null;
    }

    private teardownSteps(): void {
        const session = this.session;
        for (const sub of this.navigationSubs) sub.unsubscribe();
        this.navigationSubs = [];
        this.armed.clear();
        this.reanchoring.clear();
        if (this.projectOpenListener) {
            this.document.removeEventListener('click', this.projectOpenListener, true);
            this.projectOpenListener = null;
        }
        if (!session) return;
        if (session.onKey) {
            this.document.removeEventListener('keydown', session.onKey);
            session.onKey = null;
        }
        if (session.onResize) {
            window.removeEventListener('resize', session.onResize);
            this.document.removeEventListener('scroll', session.onResize, true);
            session.onResize = null;
        }
        session.anchorWatch?.disconnect();
        session.anchorWatch = null;
        const d = session.driver;
        session.driver = null;
        if (d?.isActive()) d.destroy();
        session.rail?.destroy();
        session.rail = null;
        session.marker?.destroy();
        session.marker = null;
        session.proxy?.destroy();
        session.proxy = null;
    }

    private cardRect(): DOMRect | null {
        const wrapper = this.document.querySelector<HTMLElement>('.driver-popover');
        if (!wrapper || wrapper.style.visibility === 'hidden' || wrapper.style.display === 'none') {
            return null;
        }
        const r = wrapper.getBoundingClientRect();
        return r.width > 0 && r.height > 0 ? r : null;
    }

    private placeCard(): void {
        const wrapper = this.document.querySelector<HTMLElement>('.driver-popover');
        if (!wrapper) return;
        const arrow = wrapper.querySelector<HTMLElement>('.driver-popover-arrow');
        const session = this.session;
        const d = session?.driver;
        const step = session?.definition.steps[session.activeIndex];
        const dock = step?.dockCard;
        const railTop = session?.rail?.dockedAt === 'top' ? TourRail.HEIGHT : 0;
        const railBottom = session?.rail?.dockedAt === 'bottom' ? TourRail.HEIGHT : 0;

        const active = d?.isActive()
            ? (d.getActiveElement() as HTMLElement | undefined)
            : undefined;
        const box = active?.isConnected ? active.getBoundingClientRect() : null;
        if (!step?.target || !box || (box.width === 0 && box.height === 0)) {
            if (arrow) arrow.style.display = 'none';
            const vw = this.document.documentElement.clientWidth;
            const vh = this.document.documentElement.clientHeight;
            const room = vh - railTop - railBottom - 32;
            if (wrapper.scrollHeight > room) fitMediaTo(wrapper, room);
            wrapper.style.maxHeight = `${room}px`;
            wrapper.style.overflowY = wrapper.scrollHeight > room ? 'auto' : '';
            const w = wrapper.offsetWidth;
            const h = Math.min(wrapper.offsetHeight, room);
            Object.assign(wrapper.style, {
                left: `${Math.round((vw - w) / 2)}px`,
                top: `${Math.round(railTop + 16 + (room - h) / 2)}px`,
                right: 'auto',
                bottom: 'auto',
            });
            return;
        }
        wrapper.style.maxHeight = '';
        wrapper.style.overflowY = '';
        // Only 'beside' narrows the card; don't let that width stick to later steps.
        if (dock !== 'beside' && wrapper.style.maxWidth === '300px') wrapper.style.maxWidth = '';

        if (!dock && d?.isActive()) {
            const target = active;
            if (target?.isConnected) {
                const padding = Number(d.getConfig().stagePadding ?? 10);
                // Measure and scroll the real element: with a frame the active element is only
                // an overlay, and scrolling its container moves nothing on the page.
                let real = step?.target
                    ? this.resolveTarget(step.target, step.targetText)
                    : undefined;
                if (real && step?.climbTo) real = real.closest<HTMLElement>(step.climbTo) ?? real;
                const anchor = real ?? target;
                const moved =
                    (step?.side === 'bottom' &&
                        this.makeRoomBelow(anchor, wrapper, railTop, railBottom)) ||
                    (step?.side === 'top' &&
                        this.makeRoomAbove(anchor, wrapper, railTop, railBottom));
                if (moved) {
                    // Let the frame catch up with the scroll, then place against the new spot.
                    requestAnimationFrame(() => requestAnimationFrame(() => this.placeCard()));
                    return;
                }
                const side = fitPopover(this.document, wrapper, arrow, {
                    target,
                    padding,
                    preferred: step?.side,
                    align: step?.align,
                    topInset: railTop,
                    bottomInset: railBottom,
                    tag: step?.advanceOnTargetClick ? session?.marker?.tagSize() : undefined,
                });
                if (side) return;
            }
        }

        if (arrow) arrow.style.display = 'none';
        const doc = this.document.documentElement;
        const w = wrapper.offsetWidth;
        const h = wrapper.offsetHeight;
        const target = d?.isActive()
            ? (d.getActiveElement() as HTMLElement | undefined)
            : undefined;
        const t = target?.isConnected ? target.getBoundingClientRect() : null;
        if (dock === 'beside' && t && target) {
            // A narrow card on the left edge, over the sidebar, pointing right at a target too
            // wide to leave room beside it. It may overlap the target's left edge a little.
            wrapper.style.maxWidth = '300px';
            const ch = wrapper.offsetHeight;
            const shownTop = Math.max(t.top, railTop + 16);
            const shownBottom = Math.min(t.bottom, doc.clientHeight - railBottom - 16);
            const mid = (shownTop + shownBottom) / 2;
            const left = 24;
            const top = Math.min(
                Math.max(24 + railTop, mid - ch / 2),
                doc.clientHeight - 24 - railBottom - ch,
            );
            Object.assign(wrapper.style, {
                top: `${Math.round(top)}px`,
                left: `${left}px`,
                right: 'auto',
                bottom: 'auto',
            });
            if (arrow) {
                const half = 8;
                arrow.style.display = '';
                arrow.style.borderWidth = `${half}px`;
                arrow.style.borderColor = 'transparent';
                for (const s of ['top', 'right', 'bottom', 'left'] as const) {
                    arrow.classList.toggle(`driver-popover-arrow-side-${s}`, s === 'left');
                }
                for (const a of ['start', 'center', 'end'] as const) {
                    arrow.classList.remove(`driver-popover-arrow-align-${a}`);
                }
                arrow.classList.remove('driver-popover-arrow-none');
                arrow.style.borderLeftColor = getComputedStyle(wrapper).backgroundColor;
                const cy = mid - top - half;
                arrow.style.top = `${Math.round(Math.min(Math.max(14, cy), ch - 2 * half - 14))}px`;
                arrow.style.left = '';
            }
            return;
        }
        if (dock === 'below' && t && target) {
            // Measure the real section: with a frame the active element is only its overlay.
            let real = step?.target ? this.resolveTarget(step.target, step.targetText) : undefined;
            if (real && step?.climbTo) real = real.closest<HTMLElement>(step.climbTo) ?? real;
            const section = real ?? target;
            const lift = section.getBoundingClientRect().top - (railTop + 32);
            // Move either way: down when the box hides under the rail, up when it sits low.
            if (Math.abs(lift) > 1) this.scrollBoxOf(section).scrollBy({ top: lift });
            const r = section.getBoundingClientRect();
            const top = Math.round(r.bottom + 24);
            const room = Math.max(240, doc.clientHeight - railBottom - 16 - top);
            wrapper.style.maxHeight = `${room}px`;
            wrapper.style.overflowY = wrapper.scrollHeight > room ? 'auto' : '';
            const wanted = step?.align === 'center' ? r.left + r.width / 2 - w / 2 : r.left + 24;
            const left = Math.min(Math.max(24, wanted), doc.clientWidth - 24 - w);
            Object.assign(wrapper.style, {
                top: `${Math.min(top, doc.clientHeight - railBottom - 16 - room)}px`,
                left: `${Math.round(left)}px`,
                right: 'auto',
                bottom: 'auto',
            });
            if (arrow) {
                const half = 8;
                arrow.style.display = '';
                arrow.style.borderWidth = `${half}px`;
                arrow.style.borderColor = 'transparent';
                for (const s of ['top', 'right', 'bottom', 'left'] as const) {
                    arrow.classList.toggle(`driver-popover-arrow-side-${s}`, s === 'bottom');
                }
                for (const a of ['start', 'center', 'end'] as const) {
                    arrow.classList.remove(`driver-popover-arrow-align-${a}`);
                }
                arrow.classList.remove('driver-popover-arrow-none');
                arrow.style.borderBottomColor = getComputedStyle(wrapper).backgroundColor;
                const tip = step?.align === 'center' ? r.left + r.width / 2 - left - half : 32;
                arrow.style.left = `${Math.round(Math.min(Math.max(14, tip), w - 2 * half - 14))}px`;
                arrow.style.top = '';
            }
            return;
        }
        if (dock === 'inside' && t && target) {
            // Point at the section's own header. With a frame the active element is only the
            // overlay, so look inside the real section (after climbTo) instead.
            let base = step?.target ? this.resolveTarget(step.target, step.targetText) : undefined;
            if (base && step?.climbTo) base = base.closest<HTMLElement>(step.climbTo) ?? base;
            const section = base ?? target;
            const title = section
                .querySelector<HTMLElement>(step?.anchor ?? '.card-title, h5')
                ?.getBoundingClientRect();
            // A short title ("Lineage") gets the card beside it; a header that runs across the
            // page gets the card just under it.
            const under = Boolean(title && title.width > doc.clientWidth / 2);
            let left = t.left + 24;
            let top = t.top + 90;
            if (title) {
                left = under ? title.left : title.right + 24;
                top = under ? title.bottom + 16 : title.top - 24;
            }
            left = Math.min(Math.max(24, left), doc.clientWidth - 24 - w);
            top = Math.min(Math.max(24 + railTop, top), doc.clientHeight - 24 - railBottom - h);
            if (arrow && title) {
                const half = 8;
                const side = under ? 'bottom' : 'right';
                arrow.style.display = '';
                arrow.style.borderWidth = `${half}px`;
                arrow.style.borderColor = 'transparent';
                for (const s of ['top', 'right', 'bottom', 'left'] as const) {
                    arrow.classList.toggle(`driver-popover-arrow-side-${s}`, s === side);
                }
                for (const a of ['start', 'center', 'end'] as const) {
                    arrow.classList.remove(`driver-popover-arrow-align-${a}`);
                }
                arrow.classList.remove('driver-popover-arrow-none');
                const surface = getComputedStyle(wrapper).backgroundColor;
                if (under) {
                    arrow.style.borderBottomColor = surface;
                    arrow.style.left = '32px';
                    arrow.style.top = '';
                } else {
                    arrow.style.borderRightColor = surface;
                    const cy = (title.top + title.bottom) / 2 - top - half;
                    arrow.style.top = `${Math.round(Math.min(Math.max(14, cy), h - 2 * half - 14))}px`;
                    arrow.style.left = '';
                }
            }
            Object.assign(wrapper.style, {
                top: `${Math.round(top)}px`,
                left: `${Math.round(left)}px`,
                right: 'auto',
                bottom: 'auto',
            });
            return;
        }
        const corners = {
            'top-right': { left: doc.clientWidth - 24 - w, top: 24 + railTop },
            'bottom-right': {
                left: doc.clientWidth - 24 - w,
                top: doc.clientHeight - 24 - railBottom - h,
            },
            'bottom-left': { left: 24, top: doc.clientHeight - 24 - railBottom - h },
            'top-left': { left: 24, top: 24 + railTop },
        };
        const overlap = (c: { left: number; top: number }) => {
            if (!t) return 0;
            const x = Math.max(0, Math.min(c.left + w, t.right) - Math.max(c.left, t.left));
            const y = Math.max(0, Math.min(c.top + h, t.bottom) - Math.max(c.top, t.top));
            return x * y;
        };
        const hinted = dock === 'top' ? 'top-right' : 'bottom-right';
        let best: keyof typeof corners = hinted;
        for (const key of Object.keys(corners) as (keyof typeof corners)[]) {
            if (overlap(corners[key]) < overlap(corners[best]) - 1) best = key;
        }
        const spot = corners[best];
        Object.assign(wrapper.style, {
            top: `${Math.round(spot.top)}px`,
            left: `${Math.round(spot.left)}px`,
            right: 'auto',
            bottom: 'auto',
        });
    }

    stop(): void {
        const session = this.session;
        if (session) {
            session.completing = true;
            this.closeScreen();
        }
        this.teardownSteps();
        this.isActive.set(false);
        this.session = null;
    }

    private nextTour(session: TourSession): LearningTourAdapter | undefined {
        const { tour, catalogue } = session;
        const track = (t: LearningTourAdapter) => t.group?.split('.')[0] || 'Tours';
        const sub = (t: LearningTourAdapter) => t.group?.split('.').slice(1).join('.') ?? '';
        const ordered = [...catalogue].sort(
            (a, b) =>
                track(a).localeCompare(track(b)) ||
                sub(a).localeCompare(sub(b)) ||
                a.sortOrder - b.sortOrder,
        );
        const index = ordered.findIndex(t => t.id === tour.id);
        if (index < 0) return undefined;
        return ordered.slice(index + 1).find(t => Boolean(t.key && getTourDefinition(t.key)));
    }

    private trackName(tour: LearningTourAdapter): string {
        return tour.group?.split('.')[0] || 'Tour';
    }

    private shortTitle(title: string): string {
        return title.replace(/^\[[^\]]*\]\s*/, '');
    }

    private progressKey(tourKey: string): string {
        const user = this.auth.currentUser()?.id ?? 'anonymous';
        return `${PROGRESS_KEY_PREFIX}.${user}.${tourKey}`;
    }

    private saveProgress(tourKey: string, index: number): void {
        try {
            localStorage.setItem(this.progressKey(tourKey), String(index));
        } catch {
            // Tour progress is optional when browser storage is unavailable.
        }
    }

    private readProgress(tourKey: string): number | undefined {
        try {
            const raw = localStorage.getItem(this.progressKey(tourKey));
            if (raw === null) return undefined;
            const index = Number(raw);
            return Number.isInteger(index) && index >= 0 ? index : undefined;
        } catch {
            return undefined;
        }
    }

    private clearProgress(tourKey: string): void {
        try {
            localStorage.removeItem(this.progressKey(tourKey));
        } catch {
            // Clearing persisted progress is best-effort when browser storage is unavailable.
        }
    }

    /**
     * Resolves a selector to the first *visible* match.
     *
     * Some targets are duplicated for responsive variants (the header search button is
     * rendered twice, one hidden per breakpoint), so `querySelector` alone can return an
     * element that is not on screen.
     */
    /**
     * Scrolls the target down when its top is still hidden under the tour rail. The rail
     * floats over the page, so the browser counts the target as visible and 'nearest'
     * leaves it cut off.
     */
    private clearRail(element: HTMLElement): void {
        const hidden = TourRail.HEIGHT + 32 - element.getBoundingClientRect().top;
        if (hidden <= 0) return;
        this.scrollBoxOf(element).scrollBy({ top: -hidden });
    }

    /**
     * Scrolls the page up when a card meant to sit below its target does not fit, as long as
     * the target stays clear of the rail. Without it the card falls back to a corner.
     */
    private makeRoomBelow(
        target: HTMLElement,
        card: HTMLElement,
        railTop: number,
        railBottom: number,
    ): boolean {
        const rect = target.getBoundingClientRect();
        const viewport = this.document.documentElement.clientHeight;
        const need = rect.bottom + 24 + card.offsetHeight - (viewport - railBottom - 16);
        const room = rect.top - (railTop + 32);
        if (need <= 1 || room <= 1) return false;
        return this.scrollMoved(target, Math.min(need, room));
    }

    /**
     * Scrolls the page down when a card meant to sit above its target does not fit, as long
     * as the target stays on screen. Without it the card falls back below or to a corner.
     */
    private makeRoomAbove(
        target: HTMLElement,
        card: HTMLElement,
        railTop: number,
        railBottom: number,
    ): boolean {
        const rect = target.getBoundingClientRect();
        const viewport = this.document.documentElement.clientHeight;
        const need = railTop + 32 + card.offsetHeight + 24 - rect.top;
        // A tall target (a chart) only needs its top part on screen, not all of it.
        const keep = Math.min(rect.height, 300);
        const room = viewport - railBottom - 16 - (rect.top + keep);
        if (need <= 1 || room <= 0 || need > room) return false;
        return this.scrollMoved(target, -need);
    }

    /** Scrolls the element's container and reports whether the page actually moved. */
    private scrollMoved(element: HTMLElement, by: number): boolean {
        const box = this.scrollBoxOf(element);
        const before = box.scrollTop;
        box.scrollBy({ top: by, behavior: 'instant' });
        return Math.abs(box.scrollTop - before) > 1;
    }

    private scrollBoxOf(element: HTMLElement): Element {
        let box: HTMLElement | null = element.parentElement;
        while (box) {
            const { overflowY } = getComputedStyle(box);
            if (/(auto|scroll)/.test(overflowY) && box.scrollHeight > box.clientHeight) return box;
            box = box.parentElement;
        }
        return this.document.scrollingElement ?? this.document.documentElement;
    }

    /** Opens a collapsed section, like Show all assets, when the step's target is inside it. */
    private revealIfHidden(selector: string, reveal: string, text?: string): void {
        if (this.resolveTarget(selector, text)) return;
        this.document.querySelector<HTMLElement>(reveal)?.click();
    }

    private resolveTarget(selector: string, text?: string): HTMLElement | undefined {
        let candidates = Array.from(this.document.querySelectorAll<HTMLElement>(selector));

        if (text) {
            // Collapse runs of whitespace on both sides. Rendered labels can carry double
            // spaces or newlines from the template that HTML renders invisibly, so a raw
            // comparison against the on-screen text silently matches nothing.
            const normalise = (value: string) => value.replace(/\s+/g, ' ').trim().toLowerCase();
            const needle = normalise(text);
            const label = (el: HTMLElement) =>
                normalise(el.querySelector('.report-name')?.textContent ?? el.textContent ?? '');

            // Exact label first: "Response Accuracy (Credit Card Queries)" is a prefix of
            // "... – constrained", so a contains-match would pick whichever came first.
            const exact = candidates.filter(el => label(el) === needle);
            candidates =
                exact.length > 0 ? exact : candidates.filter(el => label(el).includes(needle));
        }

        const match = candidates.find(el => el.offsetParent !== null) ?? candidates[0];
        return match ? this.preferRenderedControl(match) : undefined;
    }

    /**
     * Steps down to the rendered control when a `test-id` sits on an Angular component
     * host rather than the element you see.
     *
     * PrimeNG hosts such as `<p-button>` do not wrap their content: the host measures
     * shorter than the `<button>` inside it, so highlighting the host produces a cutout
     * that clips the control top and bottom while looking correct at the sides.
     */
    private preferRenderedControl(el: HTMLElement): HTMLElement {
        if (!el.tagName.startsWith('P-')) return el;

        const inner = el.querySelector<HTMLElement>('button, a, input');
        if (!inner) return el;

        // Only swap when the host is genuinely mis-measuring the control.
        return inner.getBoundingClientRect().height > el.getBoundingClientRect().height
            ? inner
            : el;
    }

    /**
     * Resolves a target, and if it is not mounted yet waits for it and re-anchors.
     *
     * Returning `undefined` leaves the popover centred with no arrow, which reads as a
     * broken step. Driver's `refresh()` re-runs this callback, so once the element
     * appears the popover snaps onto it.
     */
    private resolveOrReanchor(selector: string, text?: string): HTMLElement | undefined {
        const found = this.resolveTarget(selector, text);
        if (found) return found;

        // Keyed per selector: a single shared flag would let one pending wait block
        // every later step from ever re-anchoring.
        const pendingKey = `${selector}::${text ?? ''}`;
        if (this.reanchoring.has(pendingKey)) return undefined;
        this.reanchoring.add(pendingKey);

        void this.waitForTarget(selector, 8000, text).then(late => {
            this.reanchoring.delete(pendingKey);
            if (late) {
                const d = this.session?.driver;
                if (d?.isActive()) d.moveTo(this.session?.activeIndex ?? 0);
            }
        });

        return undefined;
    }

    private toDriveStep(step: GgxTourStep, index: number, all: GgxTourStep[]): DriveStep {
        const buttons = step.buttons ?? ['previous', 'next'];
        const nextTarget = all[index + 1]?.target;

        return {
            // Resolved lazily via callback so targets that mount later, or move between
            // responsive breakpoints, are looked up when the step runs rather than when
            element: step.target
                ? () => {
                      if (step.closeSettings) this.ensureSettingsClosed();
                      if (step.reveal)
                          this.revealIfHidden(step.target, step.reveal, step.targetText);
                      const found = this.resolveOrReanchor(step.target, step.targetText);
                      if (!found) return found;
                      if (step.climbToRow) return found.closest('tr') ?? found;
                      if (step.climbToGroup)
                          return found.closest<HTMLElement>('.p-buttongroup') ?? found;
                      const climbed = step.climbTo
                          ? (found.closest<HTMLElement>(step.climbTo) ?? found)
                          : found;
                      if (step.section)
                          return this.session?.proxy?.follow(climbed, step.target) ?? climbed;
                      if (step.frame) return this.session?.proxy?.followElement(climbed) ?? climbed;
                      return climbed;
                  }
                : undefined,
            advanceOnClick: step.advanceOnTargetClick && !nextTarget,
            popover: {
                title: step.title,
                description: step.description,
                side: step.side,
                align: step.align,
                showButtons: buttons.filter(b => b !== 'close'),
            },
        };
    }

    /**
     * Re-anchors the active step if the element it was drawn on stops being real.
     *
     * A DevExtreme grid renders its rows, then replaces the `<tr>` nodes a moment later
     * when its data source or column widths settle. A step that anchored to the first
     * render is then pinned to a detached node: Driver measures it as 0x0 at the origin,
     * so the card falls into the top-left corner with no cutout. When the watched element
     * leaves the DOM or collapses, this waits for a laid-out replacement and re-drives the
     * step so the highlight, marker and click-to-advance all move onto it.
     */
    private watchAnchor(
        session: TourSession,
        element: HTMLElement,
        stepIndex: number,
    ): MutationObserver {
        const step = session.definition.steps[stepIndex];
        let recovering = false;
        const observer = new MutationObserver(() => {
            if (recovering) return;
            if (
                this.session !== session ||
                session.activeIndex !== stepIndex ||
                !session.driver?.isActive()
            ) {
                observer.disconnect();
                return;
            }
            const rect = element.getBoundingClientRect();
            const lost = !element.isConnected || (rect.width === 0 && rect.height === 0);
            if (!lost || !step?.target) return;

            recovering = true;
            observer.disconnect();
            void this.waitForTarget(step.target, 8000, step.targetText).then(found => {
                recovering = false;
                if (!found) return;
                if (this.session !== session || session.activeIndex !== stepIndex) return;
                const d = session.driver;
                const next = session.definition.steps[stepIndex + 1];
                if (next?.target) this.armed.delete(`${next.target}::${next.targetText ?? ''}`);
                if (d?.isActive()) d.moveTo(stepIndex);
            });
        });
        observer.observe(this.document.body, { childList: true, subtree: true });
        return observer;
    }

    private applyActiveStep(): void {
        const session = this.session;
        const d = session?.driver;
        if (!session || !d?.isActive()) return;
        const { definition } = session;
        const index = session.activeIndex;
        const step = definition.steps[index];
        if (!step) return;

        if (!step.section && !step.frame) session.proxy?.stop();

        let element = step.target ? this.resolveTarget(step.target, step.targetText) : undefined;
        if (element instanceof HTMLElement && step.climbToRow) {
            element = element.closest('tr') ?? element;
        }
        if (element instanceof HTMLElement && step.climbToGroup) {
            element = element.closest<HTMLElement>('.p-buttongroup') ?? element;
        }
        if (element instanceof HTMLElement && step.climbTo) {
            element = element.closest<HTMLElement>(step.climbTo) ?? element;
        }
        if (element instanceof HTMLElement && step.scrollIntoView) {
            // Keep the target clear of the tour rail pinned to the top of the page
            const margin = element.style.scrollMarginTop;
            element.style.scrollMarginTop = `${TourRail.HEIGHT + 32}px`;
            element.scrollIntoView({ block: 'nearest', inline: 'nearest' });
            element.style.scrollMarginTop = margin;
            this.clearRail(element);
        }
        session.anchorWatch?.disconnect();
        session.anchorWatch =
            element instanceof HTMLElement ? this.watchAnchor(session, element, index) : null;
        if (element && step.section) {
            element = session.proxy?.follow(element, step.target) ?? element;
        } else if (element && step.frame) {
            element = session.proxy?.followElement(element) ?? element;
        }

        if (element instanceof HTMLElement) {
            if (!session.proxy?.owns(element)) {
                const tracked = element;
                const dockForHeader = () => {
                    if (this.session !== session || !d.isActive()) return;
                    if (!tracked.isConnected || session.proxy?.owns(tracked)) return;
                    const top = tracked.getBoundingClientRect().top;
                    session.rail?.dock(top < TourRail.HEIGHT + 32 ? 'bottom' : 'top');
                };
                dockForHeader();
                globalThis.requestAnimationFrame(dockForHeader);
            }
            const shape = stageShapeFor(element);
            d.setConfig({
                ...d.getConfig(),
                stagePadding: shape.padding,
                stageRadius: shape.radius,
            });
            d.refresh();
            session.marker?.track({
                element,
                stepNumber: this.stepNumber(definition, index),
                isAction: Boolean(step.advanceOnTargetClick),
                padding: shape.padding,
                radius: shape.radius,
            });
        } else {
            session.marker?.clear();
        }

        const next = definition.steps[index + 1];
        if (step.advanceOnTargetClick && !next) {
            this.completeOnTargetClick(element);
        } else if (step.advanceOnTargetClick) {
            this.advanceAfterNavigation(
                element,
                next?.target,
                next?.targetText,
                next?.waitForAbsent,
                next?.waitForPresent,
                next?.reveal,
            );
            if (step.advanceToProjectHome) this.advanceViaProjectHome(element);
            if (step.advanceWhenPresent) this.advanceWhenSelectorPresent(step.advanceWhenPresent);
        } else if (step.advanceOnProjectOpen) {
            this.advanceOnProjectOpen(next?.target, next?.targetText);
        } else if (step.advanceOnTargetMount) {
            this.advanceWhenTargetMounts(definition.steps, index);
        } else if (step.advanceWhenPresent) {
            this.advanceWhenSelectorPresent(step.advanceWhenPresent);
        } else if (step.advanceWhenAbsent) {
            this.advanceWhenSelectorAbsent(step.advanceWhenAbsent);
        }
    }

    private advanceWhenSelectorAbsent(selector: string): void {
        const session = this.session;
        if (!session) return;
        const stepIndex = session.activeIndex;
        const armKey = `absent::${selector}`;
        if (this.armed.has(armKey)) return;
        this.armed.add(armKey);

        void this.waitForAbsent(selector, 300000).then(() => {
            this.armed.delete(armKey);
            if (this.session !== session || !session.driver?.isActive()) return;
            if (session.activeIndex !== stepIndex) return;
            this.beginAdvanceTransition();
            session.driver.moveNext();
        });
    }

    /**
     * Advances the current step as soon as the given selector appears, and only while the
     * tour is still sitting on that step. Used for an action with no button (picking a model
     * adds a chip), so the step cannot be skipped without doing what it asks.
     */
    private advanceWhenSelectorPresent(selector: string): void {
        const session = this.session;
        if (!session) return;
        const stepIndex = session.activeIndex;
        const armKey = `present::${selector}`;
        if (this.armed.has(armKey)) return;
        this.armed.add(armKey);

        void this.waitForTarget(selector, 300000).then(found => {
            this.armed.delete(armKey);
            if (!found) return;
            if (this.session !== session || !session.driver?.isActive()) return;
            if (session.activeIndex !== stepIndex) return;
            this.beginAdvanceTransition();
            session.driver.moveNext();
        });
    }

    private advanceWhenTargetMounts(all: GgxTourStep[], index: number): void {
        const destination = all.findIndex((s, i) => i > index && Boolean(s.target));
        const target = all[destination]?.target;
        if (!target) return;

        void this.waitForTarget(target, 120000, all[destination]?.targetText).then(found => {
            if (!found) return;
            this.beginAdvanceTransition();
            globalThis.setTimeout(() => this.session?.driver?.moveTo(destination), 150);
        });
    }

    /**
     * Advances a click-to-proceed step once the user has actually acted.
     *
     * Two cases, and conflating them caused both the racing and the stalling:
     *
     * - The destination is already on this page, so the step is a plain click. Advance
     *   on the click itself.
     * - The destination is on another page. Wait for the router to report a completed
     *   navigation, then for the target to mount. Watching only for the element made
     *   steps chain, because the next target often already existed in the DOM.
     */
    private ensureSettingsClosed(): void {
        if (this.settingsClosing || !this.router.url.includes('settings:')) return;
        this.settingsClosing = true;
        void this.router.navigate(['', { outlets: { settings: null } }]).finally(() => {
            this.settingsClosing = false;
        });
    }

    private advanceOnProjectOpen(nextTarget?: string, nextText?: string): void {
        if (!nextTarget || this.projectOpenListener) return;
        const onClick = (event: Event) => {
            const option = (event.target as HTMLElement | null)?.closest?.('.p-listbox-option');
            if (!option) return;
            this.document.removeEventListener('click', onClick, true);
            this.projectOpenListener = null;
            globalThis.setTimeout(() => {
                const slug = this.readSelectedProjectSlug();
                if (slug) void this.router.navigate(['/', slug]);
            }, 60);
            void this.waitForTarget(nextTarget, 30000, nextText).then(found => {
                if (found) this.session?.driver?.moveNext();
            });
        };
        this.projectOpenListener = onClick;
        this.document.addEventListener('click', onClick, true);
    }

    private completeOnTargetClick(element?: Element): void {
        if (!(element instanceof HTMLElement) || this.armed.has('complete')) return;
        this.armed.add('complete');
        element.addEventListener(
            'click',
            () => {
                this.armed.delete('complete');
                const session = this.session;
                if (!session?.driver?.isActive() || !session.driver.isLastStep()) return;
                globalThis.setTimeout(() => {
                    if (this.session === session) this.completeSteps();
                }, 250);
            },
            { once: true, capture: true },
        );
    }

    private advanceViaProjectHome(element?: Element): void {
        if (!(element instanceof HTMLElement) || this.armed.has('project-home')) return;
        this.armed.add('project-home');
        element.addEventListener(
            'click',
            () => {
                this.armed.delete('project-home');
                globalThis.setTimeout(() => {
                    const slug = this.readSelectedProjectSlug();
                    if (slug) void this.router.navigate(['/', slug]);
                }, 60);
            },
            { once: true },
        );
    }

    private readSelectedProjectSlug(): string | null {
        try {
            return this.document.defaultView?.localStorage.getItem('selected-project-slug') ?? null;
        } catch {
            return null;
        }
    }

    private beginAdvanceTransition(): void {
        this.session?.marker?.clear();
        this.setPopoverHidden(true);
    }

    private setPopoverHidden(hidden: boolean): void {
        const pop = this.document.querySelector<HTMLElement>('.driver-popover');
        if (!pop) return;
        pop.style.transition = 'opacity 120ms ease';
        pop.style.opacity = hidden ? '0' : '1';
        pop.style.pointerEvents = hidden ? 'none' : '';
    }

    private advanceAfterNavigation(
        element?: Element,
        nextTarget?: string,
        nextText?: string,
        nextAbsent?: string,
        nextPresent?: string,
        nextReveal?: string,
    ): void {
        if (!nextTarget || !(element instanceof HTMLElement)) return;
        const armKey = `${nextTarget}::${nextText ?? ''}`;
        if (this.armed.has(armKey)) return;
        this.armed.add(armKey);
        const session = this.session;
        const stepIndex = session?.activeIndex;

        // Two triggers, one advance. The click covers menus and same-page reveals; the
        // router covers navigations where Angular tears the element down before the
        // handler runs. Without this latch both fire and the tour skips a step.
        let advanced = false;
        let started = false;
        const advanceOnce = () => {
            if (started || advanced) return;
            if (this.session !== session || session?.activeIndex !== stepIndex) return;
            started = true;
            this.beginAdvanceTransition();
            const toNextTarget = () => {
                if (nextReveal) {
                    void this.waitForTarget(nextReveal, 30000).then(() =>
                        this.revealIfHidden(nextTarget, nextReveal, nextText),
                    );
                }
                return this.waitForTarget(nextTarget, 30000, nextText).then(found => {
                    if (advanced) return;
                    if (!found) {
                        this.setPopoverHidden(false);
                        return;
                    }
                    advanced = true;
                    this.armed.delete(armKey);
                    if (this.session !== session || session?.activeIndex !== stepIndex) return;
                    session.driver?.moveNext();
                });
            };
            const afterPage = () =>
                nextPresent
                    ? this.waitForMounted(nextPresent, 30000).then(() => toNextTarget())
                    : toNextTarget();
            if (nextAbsent) {
                void this.waitForAbsent(nextAbsent, 30000).then(afterPage);
            } else {
                void afterPage();
            }
        };

        element.addEventListener('click', advanceOnce, { once: true, capture: true });

        const sub = this.router.events
            .pipe(
                filter((e): e is NavigationEnd => e instanceof NavigationEnd),
                take(1),
            )
            .subscribe(advanceOnce);

        this.navigationSubs.push(sub);
    }

    /**
     * Resolves once any element matches the selector, visible or not.
     *
     * This is the page gate, not a target: it tells the tour "the destination page has
     * rendered", so hidden markers count (a collapsed filter list is display:none but is
     * only ever rendered by the page it belongs to). Targets go through `waitForTarget`,
     * which insists on a visible, laid-out element.
     */
    private waitForMounted(selector: string, timeoutMs: number): Promise<boolean> {
        if (this.document.querySelector(selector)) return Promise.resolve(true);
        return new Promise(resolve => {
            let settled = false;
            const finish = (found: boolean) => {
                if (settled) return;
                settled = true;
                observer.disconnect();
                clearTimeout(timer);
                resolve(found);
            };
            const observer = new MutationObserver(() => {
                if (this.document.querySelector(selector)) finish(true);
            });
            observer.observe(this.document.body, { childList: true, subtree: true });
            const timer = setTimeout(() => finish(false), timeoutMs);
        });
    }

    /**
     * Resolves a selector once the element exists in the DOM.
     *
     * Driver 1.8 has no `waitForElement`, and click-to-navigate steps render their
     * target only after the next page settles. Without this the following step would
     * resolve to nothing and render as an unanchored centre modal.
     */
    private waitForTarget(
        selector: string,
        timeoutMs = 8000,
        text?: string,
    ): Promise<HTMLElement | undefined> {
        const laidOut = (el: HTMLElement) => {
            const r = el.getBoundingClientRect();
            return r.width > 0 && r.height > 0;
        };
        const settleFrame = (el: HTMLElement) =>
            new Promise<HTMLElement>(resolve => {
                requestAnimationFrame(() => requestAnimationFrame(() => resolve(el)));
            });

        const existing = this.resolveTarget(selector, text);
        if (existing && laidOut(existing)) return settleFrame(existing);

        return new Promise(resolve => {
            let settled = false;
            const finish = (el?: HTMLElement) => {
                if (settled) return;
                settled = true;
                observer.disconnect();
                clearTimeout(timer);
                if (el) void settleFrame(el).then(resolve);
                else resolve(undefined);
            };

            const observer = new MutationObserver(() => {
                const found = this.resolveTarget(selector, text);
                if (found && laidOut(found)) finish(found);
            });
            observer.observe(this.document.body, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ['class', 'style'],
            });

            // Give up rather than hang: an absent target still advances the tour.
            const timer = setTimeout(() => finish(undefined), timeoutMs);
        });
    }

    private waitForAbsent(selector: string, timeoutMs = 8000): Promise<void> {
        const gone = () => !this.resolveTarget(selector);
        if (gone()) return Promise.resolve();

        return new Promise(resolve => {
            let settled = false;
            const finish = () => {
                if (settled) return;
                settled = true;
                observer.disconnect();
                clearTimeout(timer);
                resolve();
            };

            const observer = new MutationObserver(() => {
                if (gone()) finish();
            });
            observer.observe(this.document.body, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ['class', 'style'],
            });

            const timer = setTimeout(finish, timeoutMs);
        });
    }

    /**
     * Routes to a tour's starting page and opens its record, then hands back.
     *
     * The Learning Hub can be opened from anywhere, so a tour anchored to a specific
     * object has to get itself there: navigate to the list, click the row the tour was
     * authored against, then wait for the detail page before starting.
     */
    private onEntryRoute(route: string): boolean {
        const url = this.router.url.split('?')[0].split('#')[0];
        return url === route || url.endsWith(route);
    }

    /** Select the tour's project by opening its home page, as the project switcher does. */
    private async switchProject(
        project: { slug: string; name: string },
        onReady: () => void,
    ): Promise<void> {
        const navigated = await this.router.navigateByUrl(`/${project.slug}`).catch(() => false);
        if (!navigated || !this.router.url.startsWith(`/${project.slug}`)) {
            this.toast.showWarning(
                `This tour uses the ${project.name} project, which you can't open. ` +
                    'Ask an admin for access, then start the tour again.',
            );
            return;
        }
        this.toast.showInfo(`This tour uses the ${project.name} project, so we switched to it.`);
        onReady();
    }

    private async openEntryPoint(
        definition: GgxTourDefinition,
        onReady: () => void,
    ): Promise<void> {
        const gate = definition.prerequisite;
        if (!gate) return;

        const slug = this.readSelectedProjectSlug();
        const route =
            slug &&
            definition.route.startsWith('/assets/') &&
            !definition.route.startsWith(`/${slug}/`)
                ? `/${slug}${definition.route}`
                : definition.route;
        const navigated = await this.router.navigateByUrl(route);
        if (!navigated) return;

        if (definition.autoOpen) {
            const row = await this.waitForTarget(definition.autoOpen, 15000);
            // A router link, so clicking navigates without a full page load.
            row?.click();
        }

        const ready = await this.waitForTarget(gate.target, 20000);
        if (ready) onReady();
    }
}
